import * as i0 from '@angular/core';
import { Injectable, Directive, EventEmitter, Host, Optional, SkipSelf, Input, Output, HostBinding, NgModule } from '@angular/core';

class NgxVisibilityService {
    constructor() {
        this.entryMap = new Map();
        this.observerInfoList = [];
    }
    ngOnDestroy() {
        this.entryMap.forEach((entryInfo, element) => {
            this.unobserve(element);
        });
        this.entryMap.clear();
    }
    observe(element, callback, config = {}) {
        const configCopy = {
            root: config.root || null,
            rootMargin: config.rootMargin || '0px',
            threshold: this.threshold(config.threshold),
        };
        const observerInfo = this.getObserver(configCopy);
        this.entryMap.set(element, {
            callback,
            observerInfo,
        });
        observerInfo.observer.observe(element);
        observerInfo.count += 1;
    }
    unobserve(element) {
        const entryInfo = this.entryMap.get(element);
        if (entryInfo) {
            this.entryMap.delete(element);
            entryInfo.observerInfo.count -= 1;
            if (entryInfo.observerInfo.count === 0) {
                entryInfo.observerInfo.observer.disconnect();
                this.observerInfoList = this.observerInfoList.filter(oi => oi.observer !== entryInfo.observerInfo.observer);
            }
        }
    }
    getObserver(config) {
        const configThresholdString = this.threshold(config.threshold).join(' ');
        const filteredList = this.observerInfoList.filter(oi => oi.config.root === config.root &&
            oi.config.rootMargin === config.rootMargin &&
            this.threshold(oi.config.threshold).join(' ') ===
                configThresholdString);
        if (filteredList && filteredList.length) {
            return filteredList[0];
        }
        const observer = new IntersectionObserver(toggledEntries => {
            for (const entry of toggledEntries) {
                const entryInfo = this.entryMap.get(entry.target);
                if (entryInfo) {
                    entryInfo.callback(entry.isIntersecting);
                }
            }
        }, config);
        const observerInfo = {
            observer,
            config,
            count: 0,
        };
        this.observerInfoList.push(observerInfo);
        return observerInfo;
    }
    threshold(configValue) {
        if (!configValue) {
            return [0];
        }
        if (!Array.isArray(configValue)) {
            return [configValue];
        }
        return configValue;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityService, decorators: [{
            type: Injectable
        }] });

class NgxVisibilityAnchorDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    ngxVisibilityGetElementRef() {
        return this.elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityAnchorDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.1", type: NgxVisibilityAnchorDirective, selector: "[ngxVisibilityAnchor]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityAnchorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxVisibilityAnchor]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class NgxVisibilityDirective {
    constructor(elementRef, ngxVisibilityService, ngxVisibilityAnchorDirective) {
        this.elementRef = elementRef;
        this.ngxVisibilityService = ngxVisibilityService;
        this.ngxVisibilityAnchorDirective = ngxVisibilityAnchorDirective;
        this.ngxVisibilityMargin = "0px";
        this.ngxVisibilityThreshold = 0;
        this.ngxVisibility = new EventEmitter();
        this.observing = false;
    }
    ngAfterViewInit() {
        this.observe();
    }
    ngOnChanges(changes) {
        if (this.observing &&
            (changes["ngxVisibilityMargin"] ||
                changes["ngxVisibilityThresholds"] ||
                changes["ngxVisibility"])) {
            this.unobserve();
            this.observe();
        }
    }
    ngOnDestroy() {
        this.unobserve();
    }
    observe() {
        if (!this.observing) {
            const config = {
                rootMargin: "0px",
                root: null,
                threshold: [0]
            };
            if (this.ngxVisibilityMargin) {
                config.rootMargin = this.ngxVisibilityMargin;
            }
            if (this.ngxVisibilityThreshold) {
                if (Array.isArray(this.ngxVisibilityThreshold)) {
                    config.threshold = this.ngxVisibilityThreshold;
                }
                else {
                    config.threshold = [this.ngxVisibilityThreshold];
                }
            }
            if (this.ngxVisibilityAnchorDirective) {
                config.root =
                    this.ngxVisibilityAnchorDirective.ngxVisibilityGetElementRef().nativeElement;
            }
            this.ngxVisibilityService.observe(this.elementRef.nativeElement, (isVisible) => {
                this.ngxVisibility.emit(isVisible);
            }, config);
            this.observing = true;
        }
    }
    unobserve() {
        if (this.observing) {
            this.ngxVisibilityService.unobserve(this.elementRef.nativeElement);
            this.observing = false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityDirective, deps: [{ token: i0.ElementRef }, { token: NgxVisibilityService }, { token: NgxVisibilityAnchorDirective, host: true, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.1", type: NgxVisibilityDirective, selector: "[ngxVisibility]", inputs: { ngxVisibilityMargin: "ngxVisibilityMargin", ngxVisibilityThreshold: "ngxVisibilityThreshold" }, outputs: { ngxVisibility: "ngxVisibility" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ngxVisibility]"
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: NgxVisibilityService }, { type: NgxVisibilityAnchorDirective, decorators: [{
                    type: Host
                }, {
                    type: Optional
                }, {
                    type: SkipSelf
                }] }], propDecorators: { ngxVisibilityMargin: [{
                type: Input
            }], ngxVisibilityThreshold: [{
                type: Input
            }], ngxVisibility: [{
                type: Output
            }] } });

class NgxVisibilityLazyLoadDirective {
    constructor(elementRef, ngxVisibilityService, ngxVisibilityAnchorDirective) {
        this.elementRef = elementRef;
        this.ngxVisibilityService = ngxVisibilityService;
        this.ngxVisibilityAnchorDirective = ngxVisibilityAnchorDirective;
        this.backgroundImage = "";
        this.src = "";
        this.srcset = "";
        this.ngxVisibilityMargin = "0px";
        this.ngxVisibilityThreshold = [0];
        this.backgroundImageStyle = "";
        this.loadImage = false;
        this.observing = false;
    }
    ngAfterViewInit() {
        this.observe();
    }
    ngOnChanges(changes) {
        if (this.loadImage) {
            if (changes["backgroundImage"]) {
                this.backgroundImageStyle = this.backgroundImage;
            }
            if (changes["src"]) {
                this.elementRef.nativeElement.src = this.src;
            }
            if (changes["srcset"]) {
                this.elementRef.nativeElement.srcset = this.srcset;
            }
        }
        else if (this.observing &&
            (changes["ngxVisibilityMargin"] ||
                changes["ngxVisibilityThresholds"] ||
                changes["ngxVisibility"])) {
            this.unobserve();
            this.observe();
        }
    }
    ngOnDestroy() {
        this.unobserve();
    }
    observe() {
        if (!this.observing && !this.loadImage) {
            const config = {
                rootMargin: "0px",
                root: null,
                threshold: [0]
            };
            if (this.ngxVisibilityMargin) {
                config.rootMargin = this.ngxVisibilityMargin;
            }
            if (this.ngxVisibilityThreshold) {
                if (Array.isArray(this.ngxVisibilityThreshold)) {
                    config.threshold = this.ngxVisibilityThreshold;
                }
                else {
                    config.threshold = [0];
                }
            }
            if (this.ngxVisibilityAnchorDirective) {
                config.root =
                    this.ngxVisibilityAnchorDirective.ngxVisibilityGetElementRef().nativeElement;
            }
            this.ngxVisibilityService.observe(this.elementRef.nativeElement, (isVisible) => {
                if (isVisible) {
                    this.loadImage = true;
                    this.unobserve();
                    if (this.backgroundImage) {
                        this.elementRef.nativeElement.style.backgroundImage =
                            this.backgroundImage;
                    }
                    if (this.src) {
                        this.elementRef.nativeElement.src = this.src;
                    }
                    if (this.srcset) {
                        this.elementRef.nativeElement.srcset = this.srcset;
                    }
                }
            }, config);
            this.observing = true;
        }
    }
    unobserve() {
        if (this.observing) {
            this.observing = false;
            this.ngxVisibilityService.unobserve(this.elementRef.nativeElement);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityLazyLoadDirective, deps: [{ token: i0.ElementRef }, { token: NgxVisibilityService }, { token: NgxVisibilityAnchorDirective, host: true, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.1", type: NgxVisibilityLazyLoadDirective, selector: "[ngxVisibilityLazyLoad]", inputs: { backgroundImage: "backgroundImage", src: "src", srcset: "srcset", ngxVisibilityMargin: "ngxVisibilityMargin", ngxVisibilityThreshold: "ngxVisibilityThreshold" }, host: { properties: { "style.backgroundImage": "this.backgroundImageStyle" } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityLazyLoadDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ngxVisibilityLazyLoad]"
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: NgxVisibilityService }, { type: NgxVisibilityAnchorDirective, decorators: [{
                    type: Host
                }, {
                    type: Optional
                }, {
                    type: SkipSelf
                }] }], propDecorators: { backgroundImage: [{
                type: Input
            }], src: [{
                type: Input
            }], srcset: [{
                type: Input
            }], ngxVisibilityMargin: [{
                type: Input
            }], ngxVisibilityThreshold: [{
                type: Input
            }], backgroundImageStyle: [{
                type: HostBinding,
                args: ["style.backgroundImage"]
            }] } });

class NgxVisibilityModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityModule, declarations: [NgxVisibilityAnchorDirective,
            NgxVisibilityDirective,
            NgxVisibilityLazyLoadDirective], exports: [NgxVisibilityAnchorDirective,
            NgxVisibilityDirective,
            NgxVisibilityLazyLoadDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityModule, providers: [NgxVisibilityService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.1", ngImport: i0, type: NgxVisibilityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NgxVisibilityAnchorDirective,
                        NgxVisibilityDirective,
                        NgxVisibilityLazyLoadDirective
                    ],
                    exports: [
                        NgxVisibilityAnchorDirective,
                        NgxVisibilityDirective,
                        NgxVisibilityLazyLoadDirective
                    ],
                    providers: [NgxVisibilityService]
                }]
        }] });

/*
 * Public API Surface of ngx-visibility
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxVisibilityAnchorDirective, NgxVisibilityDirective, NgxVisibilityLazyLoadDirective, NgxVisibilityModule, NgxVisibilityService };
